/**
 * Bottom detail card for a selected shop map marker.
 * Favorite star matches directory list (guest pending + signed-in API).
 * Presentation opens shop via existing customerShopUrl contract.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  classifyCatalogImageUrl,
  normalizeLogoChipBackgroundSettings,
  normalizeLogoChipRimSettings,
  resolveLocalizedName,
  toSameOriginCatalogImageUrl,
} from 'pi-kiosk-shared';
import { useTheme } from '../../app/ThemeProvider.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { isGloballySignedInChrome } from '../hub/customerHubPaths.js';
import {
  isPendingFavoriteSalesPointId,
  removePendingFavoriteSpContact,
  stagePendingFavoriteSpContact,
} from '../account/pendingFavoriteSpContacts.js';
import {
  setSalesPointFavorite,
  useSalesPointContactsByToken,
} from '../account/salesPointContactsApi.js';
import { invalidatePublicCustomerSalesPointsCache } from '../../shared/api/customerDirectoryCacheInvalidate.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { Button } from '../../shared/ui/Button.js';
import { DirectoryEntityThumb } from '../../shared/ui/DirectoryEntityThumb.js';
import { formatDistanceMeters } from '../../shared/ui/directoryListFilter.js';
import { FavoriteStarIcon } from '../../shared/ui/FavoriteStarIcon.js';
import { showToast } from '../../shared/ui/toastStore.js';
import { useStoreDirectoryThumb } from '../../shared/ui/useDirectoryEntityThumb.js';
import { readDirectoryShopsPersist } from '../../shared/session/searchPersistStorage.js';
import type { PublicCustomerSalesPointDTO } from './publicSalesPointsApi.js';
import { resolveDirectoryShopOpenAction } from './resolveDirectoryShopOpenAction.js';
import { resolveSalesPointDescription } from './resolveSalesPointDescription.js';
import { resolveShopLocationLabelWithT } from './resolveShopLocationLabel.js';

export interface ShopMapMarkerCardProps {
  readonly item: PublicCustomerSalesPointDTO;
  /** Session persist tenant segment — same as list rows (favoriteDesc cache bump). */
  readonly persistTenant: string;
  readonly hideTenantName?: boolean;
  /**
   * When set, overrides platform Obchody + brand-kit flag gating (Oblíbené map → false).
   * When omitted, chip follows platform Obchody and `showTenantLogoOnStorePhoto` (default true).
   */
  readonly showTenantChip?: boolean;
  readonly distanceMeters?: number | null;
  readonly onClose: () => void;
}

export function ShopMapMarkerCard({
  item,
  persistTenant,
  hideTenantName = false,
  showTenantChip: showTenantChipOverride,
  distanceMeters = null,
  onClose,
}: ShopMapMarkerCardProps): JSX.Element {
  const { t, i18n } = useTranslation('customer');
  const { effectiveTheme } = useTheme();
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const queryClient = useQueryClient();
  const signedIn = isGloballySignedInChrome({ session });
  /** Chip only on platform Obchody (not tenant-scoped) — same as list rows. */
  const platformObchody = !hideTenantName;
  const showTenantChipResolved =
    showTenantChipOverride ??
    (platformObchody && (item.showTenantLogoOnStorePhoto ?? true));
  const favoritesQuery = useSalesPointContactsByToken(signedIn);
  const [favoritePending, setFavoritePending] = useState(false);
  const [guestFavoriteEpoch, setGuestFavoriteEpoch] = useState(0);
  /** Signed-in add before directory favoriteCount refreshes — keyed to this marker. */
  const [optimisticAddSpId, setOptimisticAddSpId] = useState<number | null>(null);
  const [optimisticAddBaseline, setOptimisticAddBaseline] = useState(item.favoriteCount);

  // Adjust during render when the selected shop or server count changes (React docs).
  if (
    optimisticAddSpId !== null &&
    (optimisticAddSpId !== item.salesPointId ||
      item.favoriteCount !== optimisticAddBaseline)
  ) {
    setOptimisticAddSpId(null);
    setOptimisticAddBaseline(item.favoriteCount);
  }

  const favoriteIds = useMemo(
    () => new Set((favoritesQuery.data?.items ?? []).map((fav) => fav.salesPointId)),
    [favoritesQuery.data?.items],
  );
  const isFavorite = signedIn
    ? favoriteIds.has(item.salesPointId)
    : (void guestFavoriteEpoch, isPendingFavoriteSalesPointId(item.salesPointId));

  // Guest pending is never in public favoriteCount; signed-in optimistic +1 until count refreshes.
  const displayFavoriteCount = Math.max(
    0,
    item.favoriteCount +
      (!signedIn && isFavorite ? 1 : 0) +
      (optimisticAddSpId === item.salesPointId ? 1 : 0),
  );

  const displayName = resolveLocalizedName(
    item.name,
    item.nameLocales,
    i18n.language,
  );
  const tenantLabel =
    !hideTenantName && item.tenantName.length > 0 ? item.tenantName : null;
  // A13b — ONLINE_ONLY → Shipping only (never locationMissing for null ops address).
  const locationLabel = resolveShopLocationLabelWithT(
    {
      customerPresence: item.customerPresence,
      location: item.location,
      locationMissingKey: 'retail.entry.shops.locationMissing',
    },
    t,
  );
  const descriptionLabel = resolveSalesPointDescription(
    item.description,
    item.descriptionLocales,
    i18n.language,
  );
  const distanceLabel =
    typeof distanceMeters === 'number' && Number.isFinite(distanceMeters)
      ? formatDistanceMeters(distanceMeters, {
          meters: t('directory.distance.meters'),
          kilometers: t('directory.distance.kilometers'),
        })
      : null;

  const imageUrl = toSameOriginCatalogImageUrl(item.imageUrl, {
    tenantCode: item.tenantCode,
  });
  const imageUrlDark = toSameOriginCatalogImageUrl(item.imageUrlDark, {
    tenantCode: item.tenantCode,
  });
  const tenantLogoUrl = toSameOriginCatalogImageUrl(item.tenantLogoUrl, {
    tenantCode: item.tenantCode,
  });
  const tenantLogoUrlDark = toSameOriginCatalogImageUrl(item.tenantLogoUrlDark, {
    tenantCode: item.tenantCode,
  });

  const onMediaFailed = useCallback(
    (failedSrc: string): void => {
      if (classifyCatalogImageUrl(failedSrc) === 'signed_api') {
        invalidatePublicCustomerSalesPointsCache(queryClient, apiBaseUrl);
      }
    },
    [apiBaseUrl, queryClient],
  );

  // brandMark (logo-only) uses DirectoryEntityThumb dual-regime rim; storePhoto
  // keeps object-cover; Obchody overlay chip stays on --directory-thumb-chip-ring.
  const { thumbProps } = useStoreDirectoryThumb({
    imageUrl,
    imageUrlDark,
    tenantLogoUrl,
    tenantLogoUrlDark,
    monogram: {
      id: item.salesPointId,
      displayName,
      code: item.code,
    },
    showTenantChip: showTenantChipResolved,
    size: 'lg',
    logoChipRim: normalizeLogoChipRimSettings(item),
    logoChipBackground: normalizeLogoChipBackgroundSettings(item),
    logoChipRimTheme: effectiveTheme,
    onMediaFailed,
    testId: `customer-shops-directory-map-card-thumb-${item.salesPointId}`,
  });

  const shopOpenHref = useMemo(
    () =>
      resolveDirectoryShopOpenAction({
        customerShopUrl: item.customerShopUrl,
        targetTenantCode: item.tenantCode,
      })?.url,
    [item.customerShopUrl, item.tenantCode],
  );

  const handleFavorite = async (): Promise<void> => {
    if (isFavorite) {
      return;
    }
    if (!signedIn) {
      stagePendingFavoriteSpContact({
        tenantCode: item.tenantCode,
        salesPointId: item.salesPointId,
        salesPointSlug: item.code,
        code: item.code,
        name: item.name,
        nameLocales: item.nameLocales ?? null,
        location: item.location,
        customerPresence: item.customerPresence,
        tenantId: item.tenantId,
        tenantName: item.tenantName,
        customerShopUrl: item.customerShopUrl,
        imageUrl: item.imageUrl,
        imageUrlDark: item.imageUrlDark,
        tenantLogoUrl: item.tenantLogoUrl,
        tenantLogoUrlDark: item.tenantLogoUrlDark,
        showLogoChipRimLight: item.showLogoChipRimLight,
        showLogoChipRimDark: item.showLogoChipRimDark,
        logoChipRimColorLight: item.logoChipRimColorLight,
        logoChipRimColorDark: item.logoChipRimColorDark,
        showLogoChipBackgroundLight: item.showLogoChipBackgroundLight,
        showLogoChipBackgroundDark: item.showLogoChipBackgroundDark,
        logoChipBackgroundColorLight: item.logoChipBackgroundColorLight,
        logoChipBackgroundColorDark: item.logoChipBackgroundColorDark,
      });
      setGuestFavoriteEpoch((n) => n + 1);
      return;
    }
    setFavoritePending(true);
    try {
      await setSalesPointFavorite({
        apiBaseUrl,
        body: { salesPointId: item.salesPointId, favorite: true },
      });
      setOptimisticAddBaseline(item.favoriteCount);
      setOptimisticAddSpId(item.salesPointId);
      const customerKey = session?.customerId ?? 'bearer';
      await queryClient.invalidateQueries({
        queryKey: customerQueryKeys.salesPointContacts(customerKey),
      });
      if (readDirectoryShopsPersist(persistTenant)?.sort === 'favoriteDesc') {
        invalidatePublicCustomerSalesPointsCache(queryClient, apiBaseUrl);
      }
    } catch {
      showToast({
        message: t('errors.spContact.recordTransport'),
        variant: 'danger',
      });
    } finally {
      setFavoritePending(false);
    }
  };

  const handleUnfavoriteGuest = (): void => {
    removePendingFavoriteSpContact(item.salesPointId);
    setGuestFavoriteEpoch((n) => n + 1);
  };

  return (
    <div
      className={[
        'pointer-events-auto absolute inset-x-0 bottom-[var(--customer-bottom-chrome,0px)] z-[500]',
        'border-t border-[var(--color-border)] bg-[var(--color-surface)]/95 shadow-lg backdrop-blur-sm',
        'px-4 pt-3 pb-3',
      ].join(' ')}
      data-testid={`customer-shops-directory-map-card-${item.salesPointId}`}
      role="dialog"
      aria-label={displayName}
    >
      <div className="mb-3 flex gap-3">
        <DirectoryEntityThumb {...thumbProps} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold leading-snug text-[var(--color-on-surface)]">
                {displayName}
              </h2>
              {tenantLabel ? (
                <p className="mt-0.5 break-words text-sm leading-snug text-[var(--color-on-surface-muted)]">
                  {tenantLabel}
                </p>
              ) : null}
              <p className="mt-0.5 break-words text-sm leading-snug text-[var(--color-on-surface-muted)]">
                {locationLabel}
                {distanceLabel ? ` · ${distanceLabel}` : null}
              </p>
              {descriptionLabel.length > 0 ? (
                <p className="mt-1 break-words text-sm leading-snug text-[var(--color-on-surface)]">
                  {descriptionLabel}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              className="inline-flex h-11 w-11 min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full text-lg leading-none text-[var(--color-on-surface-muted)] hover:bg-[var(--color-surface-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
              aria-label={t('directory.mapView.closeCardAria')}
              data-testid="customer-shops-directory-map-card-close"
              onClick={onClose}
            >
              ×
            </button>
          </div>

          <button
            type="button"
            className={[
              'mt-2 inline-flex h-11 min-h-11 items-center gap-2 rounded-full border px-3 text-sm font-medium',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
              'disabled:cursor-not-allowed disabled:opacity-[0.55]',
              isFavorite
                ? 'border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 text-[var(--color-warning)]'
                : 'border-[var(--color-border-strong)] bg-[var(--color-surface-elevated)] text-[var(--color-on-surface)] hover:bg-[var(--color-surface-hover)]',
            ].join(' ')}
            data-testid={`customer-shops-directory-map-card-star-${item.salesPointId}`}
            title={isFavorite ? t('account.shop.unfavorite') : t('account.shop.favorite')}
            aria-label={isFavorite ? t('account.shop.unfavorite') : t('account.shop.favorite')}
            aria-pressed={isFavorite}
            disabled={favoritePending}
            onClick={(event) => {
              event.stopPropagation();
              if (isFavorite && !signedIn) {
                handleUnfavoriteGuest();
                return;
              }
              void handleFavorite();
            }}
          >
            <FavoriteStarIcon filled={isFavorite} className="h-5 w-5" />
            <span data-testid="customer-shops-directory-map-card-favorites">
              {displayFavoriteCount}
            </span>
          </button>
        </div>
      </div>
      {shopOpenHref !== undefined ? (
        <Link
          to={shopOpenHref}
          className="block w-full max-w-sm mx-auto no-underline"
          data-testid={`customer-shops-directory-map-open-${item.salesPointId}`}
        >
          <Button intent="primary" block type="button" tabIndex={-1} className="pointer-events-none w-full">
            {t('account.menu.shop.label')}
          </Button>
        </Link>
      ) : (
        <Button
          type="button"
          intent="primary"
          block
          disabled
          data-testid={`customer-shops-directory-map-open-${item.salesPointId}`}
        >
          {t('account.menu.shop.label')}
        </Button>
      )}
    </div>
  );
}
